import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { AxiosResponse } from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Chart } from "primereact/chart";
import { ProgressSpinner } from "primereact/progressspinner";
import { Utility } from "../../core/Utility";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { getDailyReportTemplate } from "../../document-templates/DailyReportTemplate";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { ReportByDay } from "../../models/Report";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getAllDocuments } from "../../services/NewVersionService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import { getAllProducts } from "../../services/ProductService";
import "./DailyReportPage.scss";

export const DailyReportPage = () => {
  const { state } = useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = new Date(businessYear, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31);
  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    if (businessYear === today.getFullYear()) {
      // Za tekuću godinu, postavi datume od 1. januara do danas
      const from = new Date(today.getFullYear(), 0, 1);
      const to = today;
      return { from, to };
    } else {
      // Za prethodne godine, postavi ceo opseg te godine
      return { from: yearStart, to: yearEnd };
    }
  };
  const [dateFrom, setDateFrom] = useState<Date>(
    getDefaultRangeForBusinessYear().from
  );
  const [dateTo, setDateTo] = useState<Date>(
    getDefaultRangeForBusinessYear().to
  );

  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [categoriesApi, setCategoriesApi] = useState<ProductCategory[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product>(
    {} as Product
  );
  const [selectedProductCategory, setSelectedProductCategory] =
    useState<ProductCategory>({} as ProductCategory);
  const [dailyData, setDailyData] = useState<ReportByDay[]>([]);
  const [totalInputNeto, setTotalInputNeto] = useState<number>(0);
  const [totalInputSrps, setTotalInputSrps] = useState<number>(0);
  const [totalOutputNeto, setTotalOutputNeto] = useState<number>(0);
  const [totalOutputSrps, setTotalOutputSrps] = useState<number>(0);
  const [processing, setProcessing] = useState(false);

  const { t } = useTranslation();

  const [pdfData, setPdfData] = useState<any>(
    getDailyReportTemplate(
      dailyData,
      selectedProduct,
      selectedProductCategory,
      totalInputNeto,
      totalInputSrps,
      totalOutputNeto,
      totalOutputSrps
    )
  );
  const [graphData, setGraphData] = useState<any>({
    labels: [],
    datasets: [
      {
        label: t("Ulaz robe neto"),
        backgroundColor: "#198754",
        data: [],
      },
      {
        label: t("Izlaz robe neto"),
        backgroundColor: "#495057",
        data: [],
      },
    ],
  });

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  useEffect(() => {
    setProcessing(true);
    Promise.all([getAllProducts(), getAllProductCategories()])
      .then(([pResp, cResp]) => {
        setProductsApi(pResp.data.result ?? []);
        setCategoriesApi(cResp.data.result ?? []);
      })
      .finally(() => setProcessing(false));
  }, []);

  useEffect(() => {
    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
  }, [businessYear]);

  useEffect(() => {
    //reset states on filter change
    setDailyData([]);
  }, [selectedProduct, selectedProductCategory]);

  useEffect(() => {
    // Set data for daily report tables
    formatDailyReportData();
  }, [selectedProduct, selectedProductCategory, dateFrom, dateTo]);

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getDailyReportTemplate(
      dailyData,
      selectedProduct,
      selectedProductCategory,
      totalInputNeto,
      totalInputSrps,
      totalOutputNeto,
      totalOutputSrps
    );
    setPdfData(newPdf);
  }, [dailyData]);

  const formatDailyReportData = () => {
    let copyOfDailyData: ReportByDay[] = [];

    // Fetching new documents using the API
    // Fetching documents separately for Mercantile Inputs and Outputs
    Promise.all([
      getAllDocuments(true, true, dateFrom, dateTo), // Fetching Mercantile Inputs
      getAllDocuments(true, false, dateFrom, dateTo), // Fetching Mercantile Outputs
    ]).then(
      ([inputResponse, outputResponse]: [AxiosResponse, AxiosResponse]) => {
        const allInputDocuments = inputResponse.data.result; // Mercantile Inputs
        const allOutputDocuments = outputResponse.data.result; // Mercantile Outputs

        // Process Mercantile Inputs
        for (const mercantileInput of allInputDocuments) {
          if (
            mercantileInput?.isDocumentFinished &&
            (selectedProductCategory?.id === undefined ||
              selectedProductCategory.id?.toString() ===
                mercantileInput.categoryId?.toString()) &&
            (selectedProduct?.id === undefined ||
              selectedProduct.id?.toString() ===
                mercantileInput?.productId?.toString())
          ) {
            const index = copyOfDailyData.findIndex(
              (item) =>
                new Date(item.date).getFullYear() ===
                  new Date(mercantileInput.date).getFullYear() &&
                new Date(item.date).getMonth() ===
                  new Date(mercantileInput.date).getMonth() &&
                new Date(item.date).getDate() ===
                  new Date(mercantileInput.date).getDate()
            );

            if (index >= 0) {
              copyOfDailyData[index].importAmount += mercantileInput.netto ?? 0;
              copyOfDailyData[index].importAmountSrps +=
                mercantileInput.srps ?? 0;
            } else {
              copyOfDailyData.push({
                id: mercantileInput.id,
                date: new Date(mercantileInput.date),
                importAmount: mercantileInput.netto ?? 0,
                importAmountSrps: mercantileInput.srps ?? 0,
                exportAmount: 0,
                exportAmountSrps: 0,
              });
            }
          }
        }

        // Process Mercantile Outputs
        for (const mercantileOutput of allOutputDocuments) {
          if (
            mercantileOutput?.isDocumentFinished &&
            (selectedProductCategory?.id === undefined ||
              selectedProductCategory.id?.toString() ===
                mercantileOutput?.categoryId?.toString()) &&
            (selectedProduct?.id === undefined ||
              selectedProduct.id?.toString() ===
                mercantileOutput?.productId?.toString())
          ) {
            const index = copyOfDailyData.findIndex(
              (item) =>
                new Date(item.date).getFullYear() ===
                  new Date(mercantileOutput.date).getFullYear() &&
                new Date(item.date).getMonth() ===
                  new Date(mercantileOutput.date).getMonth() &&
                new Date(item.date).getDate() ===
                  new Date(mercantileOutput.date).getDate()
            );

            if (index >= 0) {
              copyOfDailyData[index].exportAmount +=
                mercantileOutput.netto ?? 0;
              copyOfDailyData[index].exportAmountSrps +=
                mercantileOutput.srps ?? 0;
            } else {
              copyOfDailyData.push({
                id: mercantileOutput.id,
                date: new Date(mercantileOutput.date),
                importAmount: 0,
                importAmountSrps: 0,
                exportAmount: mercantileOutput.netto ?? 0,
                exportAmountSrps: mercantileOutput.srps ?? 0,
              });
            }
          }
        }

        // Sort the data by date (newest first)
        copyOfDailyData.sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );

        // Calculate totals
        let newTotalInputNeto = 0;
        let newTotalInputSrps = 0;
        let newTotalOutputNeto = 0;
        let newTotalOutputSrps = 0;

        for (const item of copyOfDailyData) {
          newTotalInputNeto += item.importAmount;
          newTotalInputSrps += item.importAmountSrps;
          newTotalOutputNeto += item.exportAmount;
          newTotalOutputSrps += item.exportAmountSrps;
        }

        setTotalInputNeto(newTotalInputNeto);
        setTotalInputSrps(newTotalInputSrps);
        setTotalOutputNeto(newTotalOutputNeto);
        setTotalOutputSrps(newTotalOutputSrps);

        setDailyData(copyOfDailyData);

        // Update graph data
        const newGraphData = { ...graphData };
        newGraphData.labels = [];
        newGraphData.datasets[0].data = [];
        newGraphData.datasets[1].data = [];

        for (const item of copyOfDailyData) {
          newGraphData.labels.push(Utility.formatDate(new Date(item.date)));
          newGraphData.datasets[0].data.push(item.importAmount);
          newGraphData.datasets[1].data.push(item.exportAmount);
        }

        setGraphData(newGraphData);
      }
    );
  };

  const handleProductChange = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(selectedProduct);
    copyOfNewProduct = productsApi.find((item) => item.id == value) as Product;
    setSelectedProduct(copyOfNewProduct);
  };

  const handleProductCategoryChange = (prop: string, value: any) => {
    setSelectedProduct({} as Product);
    const copyOfNewProductCategory = categoriesApi.find(
      (item) => item.id == value
    ) as ProductCategory;
    setSelectedProductCategory(copyOfNewProductCategory);
  };

  const formatDate = (rowData: any) => {
    return Utility.formatDate(new Date(rowData.date));
  };

  const importAmount = (rowData: ReportByDay) => {
    return Utility.addThousandSeparator(rowData.importAmount);
  };

  const exportAmount = (rowData: ReportByDay) => {
    return Utility.addThousandSeparator(rowData.exportAmount);
  };

  const importAmountSrps = (rowData: ReportByDay) => {
    return Utility.addThousandSeparator(rowData.importAmountSrps);
  };

  const exportAmountSrps = (rowData: ReportByDay) => {
    return Utility.addThousandSeparator(rowData.exportAmountSrps);
  };

  const {
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  return (
    <PageLayout>
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-shop-window header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-title">
              {t("Ulaz/izlaz merkantile u svim magacinima po danima za")}
            </span>
            <DropdownInput
              id="productCategory"
              value={selectedProductCategory?.id}
              onValueChange={handleProductCategoryChange}
              optionValue="id"
              optionLabel="name"
              options={categoriesApi.filter(
                (item) => item.type == ProductType.MercantileGoods
              )}
              filter={false}
              labelText=""
              placeholderText={t("grupu robe")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={processing}
            />
            <span className="header-elements"></span>
            <DropdownInput
              id="product"
              value={selectedProduct?.id}
              onValueChange={handleProductChange}
              optionValue="id"
              optionLabel="name"
              options={productsApi.filter(
                (item) => item.category?.id === selectedProductCategory?.id
              )}
              filter={false}
              labelText=""
              placeholderText={t("Naziv robe")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={selectedProductCategory?.name === undefined}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (downloadPdfDocument.current) {
                  // @ts-ignore
                  downloadPdfDocument.current();
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (printPdfDocument.current) {
                  // @ts-ignore
                  printPdfDocument.current();
                }
              }}
            />
          </div>
        </div>
        <div className="wrapper-data-table">
          {processing ? (
            <ProgressSpinner
              style={{ width: "100px", height: "100px", color: "green" }}
              strokeWidth="4"
              animationDuration=".8s"
            />
          ) : (
            <DataTable
              value={dailyData}
              //footer={renderFooter}
              showGridlines
              stripedRows
            >
              <Column
                body={formatDate}
                header={t("Datum")}
                footer={t("Ukupno")}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={importAmount}
                header={t("Ulaz neto")}
                footer={Utility.addThousandSeparator(totalInputNeto)}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={importAmountSrps}
                header={t("Ulaz srps")}
                footer={Utility.addThousandSeparator(totalInputSrps)}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={exportAmount}
                header={t("Izlaz neto")}
                footer={Utility.addThousandSeparator(totalOutputNeto)}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={exportAmountSrps}
                header={t("Izlaz srps")}
                footer={Utility.addThousandSeparator(totalOutputSrps)}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
            </DataTable>
          )}
        </div>
        <div className="document-preview">
          <PdfPreview
            pdfDefinition={pdfData}
            downloadDocument={downloadPdfDocument}
            printDocument={printPdfDocument}
            documentName={`${
              selectedProduct.name ?? selectedProductCategory.name ?? ""
            } ${t("ulaz/izlaz po danima")} ${Utility.formatDate(
              new Date()
            )}.pdf`}
          />
        </div>
        {dailyData.length > 0 && (
          <div className="card">
            <Chart type="bar" data={graphData} />
          </div>
        )}
      </div>
    </PageLayout>
  );
};
